import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Subject } from 'rxjs';
import { mergeMap, switchMap, take, takeUntil, toArray } from 'rxjs/operators';
import { TableCanDeactivate } from '~app/core/utils/can-deactivate/table-can-deactivate';
import { MetadataDefinitionService } from '~core/data/metadata-definition.service';
import { MetadataSetService } from '~core/data/metadata-set.service';
import { MetadataService } from '~core/data/metadata.service';
import { Application } from '~core/models/application.model';
import {
  MetadataDefinition,
  MetadataTypeList
} from '~core/models/metadata-definition.model';
import { Metadata } from '~core/models/metadata.model';
import { TableDataSource } from '~core/utils/material-table/table-data-source';
import { MetadataIoService } from '~core/utils/metadata-io/metadata-io.service';
import { ApplicationService } from '../application.service';

class ExportMetadata {
  definitionName: string;
  value: string;
}

class ExportMetadataSet {
  name: string;
  comments: string;
  metadata: ExportMetadata[];
}

class ExportAppMetadata {
  base: MetadataDefinition[];
  sets: ExportMetadataSet[];
}

@Component({
  selector: 'ngx-metadata-definition-tab',
  templateUrl: './metadata-definition-tab.component.html',
  styleUrls: ['./metadata-definition-tab.component.scss']
})
export class MetadataDefinitionTabComponent extends TableCanDeactivate
  implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();

  displayedColumns: string[] = [
    'keyName',
    'keyType',
    'keyDefault',
    'actionsColumn'
  ];

  @Input() application: Application;
  metadatadefinitions: MetadataDefinition[];

  dataSource: TableDataSource<MetadataDefinition>;

  typeList = MetadataTypeList;

  activeEdits = 0;
  @Output() activeEdits$ = new EventEmitter<number>();

  constructor(
    private _applicationService: ApplicationService,
    private _route: ActivatedRoute,
    private metadatadefinitionService: MetadataDefinitionService,
    private metadataIO: MetadataIoService,
    private metadataService: MetadataService,
    private metadatasetService: MetadataSetService
  ) {
    super();
    this._route.parent.paramMap
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap((params: ParamMap) =>
          this._applicationService.getApp(+params.get('id'))
        )
      )
      .subscribe(app => {
        if (app) {
          this.application = app;
          this.updateData();
        }
      });

    this.activeEdits$.emit(this.activeEdits);
    this.dataSource = new TableDataSource([], MetadataDefinition);
    this.dataSource.datasourceSubject
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(metadatadefinitions => {
        this.metadatadefinitions = metadatadefinitions;
      });
  }

  ngOnInit() {}

  updateData() {
    this.metadatadefinitionService
      .listall(this.application.applicationId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(data => {
        const metadatadefinitions = data.sort((a, b) =>
          a.keyName > b.keyName ? 1 : b.keyName > a.keyName ? -1 : 0
        );
        this.dataSource.updateDatasource(metadatadefinitions);
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  createOrEditConfirm(row) {
    row.currentData.keyName = row.currentData.keyName.replace(/ /g, '_');
    if (row.id === -1) {
      this.activeEdits++;
      row.currentData.applicationId = this.application.applicationId;
    }
    this.metadatadefinitionService
      .edit(this.application.applicationId, row.currentData)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        row.confirmEditCreate();
        this.activeEdits--;
      });
    this.activeEdits$.emit(this.activeEdits);
  }

  startEditConfirm(row) {
    row.startEdit();
    this.activeEdits++;
    this.activeEdits$.emit(this.activeEdits);
  }

  cancelOrDeleteConfirm(row) {
    if (row.editing) {
      // Edit cancel
      row.cancelOrDelete();
      this.activeEdits--;
    } else {
      // Delete Row
      this.metadatadefinitionService
        .delete(
          this.application.applicationId,
          row.currentData.metadataDefinitionId
        )
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(() => {
          row.cancelOrDelete();
        });
    }
    this.activeEdits$.emit(this.activeEdits);
  }

  exportMetadata() {
    const exportData = new ExportAppMetadata();
    exportData.base = this.metadatadefinitions;
    exportData.sets = [];

    this.metadatasetService
      .list(this.application.applicationId)
      .pipe(
        take(1),
        mergeMap(metadataSets => metadataSets),
        mergeMap(
          metadataSet =>
            this.metadataService
              .list(this.application.applicationId, metadataSet.metadataSetId)
              .pipe(take(1)),
          (metadataSet, metadatas) => {
            const exportMetadataSet = new ExportMetadataSet();
            exportMetadataSet.name = metadataSet.name;
            exportMetadataSet.comments = metadataSet.comments;
            exportMetadataSet.metadata = [];
            metadatas.forEach((metadata: Metadata) => {
              const exportMetadata = new ExportMetadata();
              exportMetadata.definitionName = metadata.definitionName;
              exportMetadata.value = metadata.value;
              exportMetadataSet.metadata.push(exportMetadata);
            });
            return exportMetadataSet;
          }
        ),
        toArray()
      )
      .subscribe(sets => {
        exportData.sets = sets;

        const newBlob = new Blob([JSON.stringify(exportData, null, 2)], {
          type: 'application/json'
        });

        // IE doesn't allow using a blob object directly as link href
        // instead it is necessary to use msSaveOrOpenBlob
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(
            newBlob,
            this.application.name + '_metaData.json'
          );
          return;
        }

        // For other browsers:
        // Create a link pointing to the ObjectURL containing the blob.
        const data = window.URL.createObjectURL(newBlob);

        const link = document.createElement('a');
        link.href = data;
        link.download = this.application.name + '_metaData.json';
        // this is necessary as link.click() does not work on the latest firefox
        link.dispatchEvent(
          new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window
          })
        );

        setTimeout(function() {
          // For Firefox it is necessary to delay revoking the ObjectURL
          window.URL.revokeObjectURL(data);
          link.remove();
        }, 100);
      });
  }

  importMetadata() {
    this.metadataIO.importMetadata(this.application.applicationId).then(
      () => this.updateData(),
      err => console.log(err)
    );
  }
}
